import { useQuery } from '@tanstack/react-query'
import { fetchPublishedCurrent } from '@/api/client'
import { EmptyState } from '@/components/EmptyState'
import { ErrorState } from '@/components/ErrorState'
import { LoadingState } from '@/components/LoadingState'
import { Money } from '@/components/Money'
import { useAuth } from '@/auth/useAuth'
import { isViewerUser, isFinanceUser } from '@/api/types'
import { formatDateTime } from '@/lib/format-date'
import { formatMoney } from '@/lib/format-money'

function moneyFromDisplay(display: string, currency: string): string {
  const cleaned = display.replace(/[^0-9.-]/g, '')
  const numeric = Number(cleaned)
  if (!Number.isFinite(numeric)) return display || '—'
  return formatMoney(numeric, currency)
}

/** Read a headline total that may be a number or a legacy display string. */
function _headlineAmount(
  totals: Record<string, unknown>,
  primary: string,
  legacy?: string,
): number | null {
  const raw = totals[primary] ?? (legacy ? totals[legacy] : undefined)
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw
  if (typeof raw === 'string') {
    const cleaned = raw.replace(/[^0-9.-]/g, '')
    const numeric = Number(cleaned)
    if (Number.isFinite(numeric)) return numeric
  }
  return null
}

export function PublishedPage() {
  const { user } = useAuth()
  const reviewerOnly = isViewerUser(user) && !isFinanceUser(user)

  const publishedQuery = useQuery({
    queryKey: ['finance', 'published', 'current'],
    queryFn: fetchPublishedCurrent,
  })

  const published = publishedQuery.data ?? null
  const totals = published?.headline_totals as Record<string, unknown> | null | undefined
  const currency = String(totals?.currency ?? 'USD')

  return (
    <section className="page page--scroll">
      <header className="page__header">
        <div>
          <h1 className="page__title">Published forecast</h1>
          <p className="page__subtitle">
            {published
              ? `Published ${formatDateTime(published.published_at)}`
              : reviewerOnly
                ? 'Read-only commentary and snapshot totals for reviewers.'
                : 'Latest published snapshot (Finance view).'}
          </p>
        </div>
      </header>

      {publishedQuery.isLoading && (
        <LoadingState variant="skeleton-block" label="Loading published forecast…" />
      )}

      {publishedQuery.isError && (
        <ErrorState
          message="Could not load published forecast. It may not exist yet."
          onRetry={() => void publishedQuery.refetch()}
        />
      )}

      {!publishedQuery.isLoading && !published && !publishedQuery.isError && (
        <EmptyState title="No forecast has been published yet." />
      )}

      {published && (
        <article className="published-card">
          <header className="published-card__header">
            <h2 className="published-card__title">Commentary</h2>
          </header>
          <p className="published-card__commentary">
            {published.commentary?.trim() ? published.commentary : '(No commentary provided.)'}
          </p>

          {totals && (
            <dl className="published-card__totals">
              {_headlineAmount(totals, 'weighted_total', 'total_weighted_display') != null && (
                <>
                  <dt>Weighted total</dt>
                  <dd>
                    <Money
                      amount={
                        _headlineAmount(
                          totals,
                          'weighted_total',
                          'total_weighted_display',
                        ) as number
                      }
                      currency={currency}
                    />
                  </dd>
                </>
              )}
              {_headlineAmount(totals, 'committed_total') != null && (
                <>
                  <dt>Committed</dt>
                  <dd>
                    <Money
                      amount={_headlineAmount(totals, 'committed_total') as number}
                      currency={currency}
                    />
                  </dd>
                </>
              )}
              {_headlineAmount(totals, 'upside_total') != null && (
                <>
                  <dt>Upside</dt>
                  <dd>
                    <Money
                      amount={_headlineAmount(totals, 'upside_total') as number}
                      currency={currency}
                    />
                  </dd>
                </>
              )}
            </dl>
          )}

          {published.lines.length > 0 && (
            <div className="table-wrap">
              <table className="table table--dense">
                <caption className="visually-hidden">Published opportunity snapshot</caption>
                <thead>
                  <tr>
                    <th scope="col">Opportunity</th>
                    <th scope="col" className="table__num">
                      Year total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {published.lines.map((line) => (
                    <tr key={line.opportunity_id}>
                      <td>{line.name}</td>
                      <td className="table__num money">
                        {moneyFromDisplay(line.year_total_display, currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </article>
      )}
    </section>
  )
}
